import dayjs from 'dayjs';

/**
 * Snap a scheduled end date onto the recurrence of its start date.
 *
 * The one implementation of the end-date clamp. ControlledRecurringDatePicker
 * applies it on mount and whenever the start date or frequency changes; a
 * caller that can save before the (lazy) picker has mounted applies it to its
 * draft so the saved schedule is the one the picker would have shown.
 *
 * - week: the end moves to the start's day of the week.
 * - month / year: the end takes the start's day of the month, capped at the
 *   end month's last day; a year also takes the start's month.
 * - an end on or before the start becomes one period after the start.
 *
 * @param {object} args
 * @param {*} args.startValue start date (anything dayjs parses); empty means today
 * @param {*} args.endValue end date; null, undefined and 'INFINITY' are never snapped
 * @param {'day'|'week'|'month'|'year'} args.timePicker the recurrence period
 * @param {import('dayjs').Dayjs} args.today start of the current day
 * @returns {import('dayjs').Dayjs|null} the snapped end date, or null when the
 *   end date already sits on the recurrence (nothing to change)
 */
export const snapScheduledEndDate = ({ startValue, endValue, timePicker, today }) => {
  if (endValue === null || endValue === undefined || endValue === 'INFINITY') {
    return null;
  }

  const startingPosition = (startValue === null || startValue === undefined)
    ? today
    : dayjs(startValue).startOf('day');
  const endingPosition = dayjs(endValue).startOf('day');

  let newEndValue = endingPosition;

  if (timePicker === 'year' && endingPosition.month() !== startingPosition.month()) {
    newEndValue = newEndValue.month(startingPosition.month());
  }

  if (timePicker === 'week') {
    const difference = startingPosition.day() - dayjs(endValue).day();
    newEndValue = dayjs(endValue).add(difference, 'day');
  }

  if (timePicker === 'month' || timePicker === 'year') {
    let dayOfMonth = startingPosition.date();
    const endOfMonth = newEndValue.endOf('month').date();
    if (dayOfMonth > endOfMonth) {
      dayOfMonth = endOfMonth;
    }
    newEndValue = newEndValue.date(dayOfMonth);
  }

  if (newEndValue.isBefore(startingPosition) || newEndValue.isSame(startingPosition)) {
    newEndValue = dayjs(startingPosition);
    if (timePicker === 'day') {
      newEndValue = newEndValue.add(1, 'day');
    }
    if (timePicker === 'week') {
      newEndValue = newEndValue.add(1, 'week');
    }
    if (timePicker === 'month') {
      newEndValue = newEndValue.add(1, 'month');
    }
    if (timePicker === 'year') {
      newEndValue = newEndValue.add(1, 'year');
    }
  }

  if (newEndValue.format('YYYY-MM-DD') !== endingPosition.format('YYYY-MM-DD')) {
    return newEndValue;
  }
  return null;
};
